import loginHtml from './LoginComponent.html';
import { showLandingView } from "../ReportList/ReportListComponent";
import { showToast } from "../../utils/ui-utils";
import { stateManager } from "../../utils/state-manager";
import { resetHeader } from "../../utils/header-manager";
import { storeSession, warnIfDisconnected } from "../../utils/session";

/* global Office */

// Matches Angular's Validators.email closely enough for the same UX.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export async function loadLoginComponent(onLoginSuccess: () => void): Promise<void> {
    const container = document.getElementById("component-container");
    if (!container) return;

    container.innerHTML = loginHtml;
    container.classList.add('login-view');
    stateManager.setView('login');

    const form          = document.getElementById("login-form") as HTMLFormElement | null;
    const emailInput    = document.getElementById("username") as HTMLInputElement | null;
    const passwordInput = document.getElementById("password") as HTMLInputElement | null;
    const emailError    = document.getElementById("email-error") as HTMLElement | null;
    const passwordError = document.getElementById("password-error") as HTMLElement | null;
    const loginError    = document.getElementById("login-error") as HTMLElement | null;
    const loginButton   = document.getElementById("login-button") as HTMLButtonElement | null;
    const loginLabel    = loginButton?.querySelector('.login-label') as HTMLElement | null;
    const xeroButton    = document.getElementById("xero-signin-button") as HTMLButtonElement | null;
    const passwordToggle = document.getElementById("password-toggle") as HTMLElement | null;
    const eyeShowIcon    = document.getElementById("password-icon-show") as HTMLElement | null;
    const eyeHideIcon    = document.getElementById("password-icon-hide") as HTMLElement | null;

    // Mirrors Angular's dirty/touched gating: an error only becomes visible once
    // the user has typed in the field or left it.
    const touched = { email: false, password: false };
    let loading = false;

    // ---- Validation --------------------------------------------------------
    const emailErrorText = (): string | null => {
        const value = emailInput?.value.trim() ?? "";
        if (!value) return "Work email is required";
        if (!EMAIL_RE.test(value)) return "Please enter a valid email";
        return null;
    };

    const passwordErrorText = (): string | null =>
        passwordInput?.value ? null : "Password is required";

    const renderField = (
        input: HTMLInputElement | null,
        errorEl: HTMLElement | null,
        message: string | null,
        isTouched: boolean
    ) => {
        const show = !!message && isTouched;
        if (errorEl) {
            errorEl.textContent = show ? message! : "";
            errorEl.style.display = show ? "block" : "none";
        }
        input?.classList.toggle("field__input--invalid", show);
    };

    const isFormValid = () => !emailErrorText() && !passwordErrorText();

    const render = () => {
        renderField(emailInput, emailError, emailErrorText(), touched.email);
        renderField(passwordInput, passwordError, passwordErrorText(), touched.password);

        if (loginButton) {
            // Same as the web app: the CTA looks disabled while the form is
            // incomplete, but only carries the disabled attribute while a
            // sign-in is in flight.
            loginButton.classList.toggle("primary-btn--disabled", !isFormValid() || loading);
            loginButton.disabled = loading;
        }
        if (loginLabel) {
            loginLabel.innerHTML = loading ? "Signing in…" : 'Log in <span aria-hidden="true">&rarr;</span>';
        }
    };

    const hideServerError = () => {
        if (!loginError) return;
        loginError.textContent = "";
        loginError.style.display = "none";
    };

    const showServerError = (message: string) => {
        if (!loginError) return;
        loginError.textContent = message;
        loginError.style.display = "block";
    };

    emailInput?.addEventListener("blur", () => { touched.email = true; render(); });
    emailInput?.addEventListener("input", () => {
        touched.email = true; // Angular marks the control dirty on the first keystroke.
        hideServerError();
        render();
    });
    passwordInput?.addEventListener("blur", () => { touched.password = true; render(); });
    passwordInput?.addEventListener("input", () => {
        touched.password = true;
        hideServerError();
        render();
    });

    // ---- Password show/hide toggle ----------------------------------------
    const togglePasswordVisibility = () => {
        if (!passwordInput || !passwordToggle) return;
        const nowVisible = passwordInput.type === "password";
        passwordInput.type = nowVisible ? "text" : "password";
        if (eyeShowIcon) eyeShowIcon.style.display = nowVisible ? "none" : "block";
        if (eyeHideIcon) eyeHideIcon.style.display = nowVisible ? "block" : "none";
        passwordToggle.setAttribute("aria-label", nowVisible ? "Hide password" : "Show password");
    };
    passwordToggle?.addEventListener("click", togglePasswordVisibility);
    passwordToggle?.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            togglePasswordVisibility();
        }
    });

    // ---- Signed in -----------------------------------------------------------
    // Both ways in finish here, so they leave the same session behind.
    const completeSignIn = async (accessToken: string, rawUser: any) => {
        const user = await storeSession(accessToken, rawUser);
        container.classList.remove('login-view');
        await onLoginSuccess();
        showLandingView();
        warnIfDisconnected(user);
    };

    // ---- Xero OAuth --------------------------------------------------------
    // Runs in an Office dialog on the add-in's own xero-signin.html, which goes to Xero through
    // the API and hands the Synced session back with messageParent. Office only opens a dialog
    // on the add-in's own domain, which is why it does not open login.xero.com directly.
    let xeroDialogOpen = false;
    xeroButton?.addEventListener("click", () => {
        if (xeroDialogOpen) return;
        hideServerError();

        if (typeof Office === 'undefined' || !Office.context?.ui?.displayDialogAsync) {
            showServerError("Sign in with Xero needs the add-in to be running in Excel.");
            return;
        }

        const url = new URL("xero-signin.html", window.location.href).toString();
        xeroDialogOpen = true;
        Office.context.ui.displayDialogAsync(url, { height: 70, width: 40, promptBeforeOpen: false }, (result) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
                xeroDialogOpen = false;
                showServerError("Could not open the Xero sign-in window. Please try again.");
                return;
            }

            const dialog = result.value;

            dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg: any) => {
                dialog.close();
                xeroDialogOpen = false;

                let message: any = null;
                try { message = JSON.parse(arg.message); } catch { /* handled below */ }

                if (message?.status !== "success") {
                    showServerError(message?.error || "Xero sign-in failed. Please try again.");
                    return;
                }

                loading = true;
                render();
                try {
                    await completeSignIn(message.accessToken, message.user);
                } catch {
                    showToast("Signed in, but Synced could not load. Please try again.", true);
                } finally {
                    loading = false;
                    render();
                }
            });

            // Closed by the user, or the dialog failed to navigate.
            dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
                xeroDialogOpen = false;
            });
        });
    });

    // ---- Submit ------------------------------------------------------------
    const submitHandler = async (e?: Event) => {
        e?.preventDefault();
        hideServerError();

        // Angular's markAllAsTouched() equivalent.
        touched.email = true;
        touched.password = true;
        render();
        if (!isFormValid() || !loginButton || loading) return;

        loading = true;
        render();

        const email    = emailInput!.value.trim().toLowerCase();
        const password = passwordInput!.value;

        try {
            const { environment } = await import('../../../environments/environment');
            const response = await fetch(environment.baseUrl + "api/token", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password, accountRemember: true })
            });
            if (!response.ok) {
                showServerError("Login failed. Please check your credentials.");
                return;
            }
            const result = await response.json();
            if (result.status !== 0) {
                showServerError(result.message || "Login failed.");
                return;
            }
            await completeSignIn(result.data.access_token, result.data.user);
        } catch {
            showToast("Network error. Please try again.", true);
        } finally {
            loading = false;
            render();
        }
    };

    form?.addEventListener("submit", submitHandler);
    // `loginButton` is inside the form as type="submit" so form submission handles it,
    // but keep a direct click fallback for safety with older Office webviews.
    loginButton?.addEventListener("click", (e) => {
        if (form) return; // form handler will take over via submit
        submitHandler(e);
    });

    render();
}

/**
 * Sign out the user: clears auth state and returns to login view.
 */
export async function signOut(): Promise<void> {
    localStorage.removeItem("AccessToken");
    localStorage.removeItem("User");
    localStorage.removeItem("SelectedOrganization");
    localStorage.removeItem("organizationName");
    localStorage.removeItem("SelectedOrganizationName");
    sessionStorage.clear();
    resetHeader();
    stateManager.reset();

    await loadLoginComponent(async () => {
        const { loadReportsListComponent } = await import("../ReportList/ReportListComponent");
        await loadReportsListComponent(() => {});
    });
}
